import { type FC, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Eye, EyeOff, Settings } from 'lucide-react';
import { JoinTreeLabel } from './JoinTreeLabel';
import { usePipelineNodes } from '../hooks/usePipelineNodes';
import type { DimensionJoinTree, JoinPathOption } from '../services/measure.service';

interface DimensionJoinPathsTableProps {
  dimensionJoinTrees: DimensionJoinTree[];
  joinPathOptions: JoinPathOption[];
  metricPipelineNodeId: string;
  whitelistNodeIds: string[];
  onEnableDimension: (idx: number) => void;
  onDisableDimension: (idx: number) => void;
  onSelectPath: (pipelineNodeId: string) => void;
}

export const DimensionJoinPathsTable: FC<DimensionJoinPathsTableProps> = ({
  dimensionJoinTrees,
  joinPathOptions,
  metricPipelineNodeId,
  whitelistNodeIds,
  onEnableDimension,
  onDisableDimension,
  onSelectPath,
}) => {
  const { data: nodesData } = usePipelineNodes({ page: 1, pageSize: 1000 });

  const nodesById = useMemo(() => {
    if (!nodesData?.records) return new Map();
    return new Map(nodesData.records.map((n) => [n.id, n]));
  }, [nodesData]);

  const joinPathOptionsByNodeId = useMemo(() => {
    const map = new Map<string, JoinPathOption>();
    joinPathOptions.forEach((opt) => map.set(opt.pipeline_node_id, opt));
    return map;
  }, [joinPathOptions]);

  const getHighlightNodeIds = useCallback(
    (pipelineNodeId: string) => {
      return [pipelineNodeId, metricPipelineNodeId, ...whitelistNodeIds];
    },
    [metricPipelineNodeId, whitelistNodeIds]
  );

  if (dimensionJoinTrees.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No dimension connections available. Save the metric after selecting an
        entity to load available dimensions.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[25%]">Entity</TableHead>
          <TableHead className="w-[55%]">Join Path</TableHead>
          <TableHead className="w-[20%] text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {dimensionJoinTrees.map((djt, idx) => {
          const node = nodesById.get(djt.pipeline_node_id);
          const nodeName = node?.label || node?.name || djt.pipeline_node_id;
          const pathOptions = joinPathOptionsByNodeId.get(djt.pipeline_node_id);
          const pathCount = pathOptions?.possible_paths?.length || 0;

          return (
            <TableRow key={djt.pipeline_node_id}>
              <TableCell className="font-medium">{nodeName}</TableCell>
              <TableCell>
                {djt.ignore ? (
                  <Badge variant="secondary">Disabled</Badge>
                ) : djt.join_tree ? (
                  <div className="flex items-center gap-2">
                    {pathCount > 1 && (
                      <Badge
                        variant="outline"
                        className="cursor-pointer hover:bg-primary/10 flex-shrink-0"
                        onClick={() => onSelectPath(djt.pipeline_node_id)}
                      >
                        {pathCount} paths
                      </Badge>
                    )}
                    <JoinTreeLabel
                      joinTree={djt.join_tree}
                      highlightNodeIds={getHighlightNodeIds(djt.pipeline_node_id)}
                    />
                  </div>
                ) : (
                  <Badge
                    variant="destructive"
                    className="cursor-pointer"
                    onClick={() => onSelectPath(djt.pipeline_node_id)}
                  >
                    Not configured ({pathCount} available)
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  {djt.ignore ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEnableDimension(idx)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Enable
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSelectPath(djt.pipeline_node_id)}
                        title="Configure join path"
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDisableDimension(idx)}
                      >
                        <EyeOff className="h-4 w-4 mr-1" />
                        Disable
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
